import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../../../components/common/FeatherIcon';
import { groupHistory } from './agentModel';

const Item = ({ item, active, onOpen, onDelete, onTogglePin, t }) => {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <div className="sa3-item-confirm" role="group" aria-label={t('agent.deleteConversation')}>
        <span>{t('agent.confirmDelete')}</span>
        <button type="button" className="danger" onClick={() => { setConfirming(false); onDelete(item); }}>{t('agent.delete')}</button>
        <button type="button" onClick={() => setConfirming(false)}>{t('agent.cancel')}</button>
      </div>
    );
  }
  return (
    <div className={`sa3-item${active ? ' on' : ''}`}>
      <button type="button" className="sa3-item-main" onClick={() => onOpen(item)} aria-current={active ? 'true' : undefined}>
        {item.bookmarked && <FeatherIcon name="bookmark" size={14} />}
        <span>{item.title || t('agent.untitled')}</span>
      </button>
      <div className="sa3-item-acts">
        <button type="button" className="sa3-ibtn sm" onClick={() => onTogglePin(item)} aria-label={item.bookmarked ? t('agent.unpin') : t('agent.pin')} title={item.bookmarked ? t('agent.unpin') : t('agent.pin')}>
          <FeatherIcon name="bookmark" size={14} />
        </button>
        <button type="button" className="sa3-ibtn sm" onClick={() => setConfirming(true)} aria-label={t('agent.deleteConversation')} title={t('agent.deleteConversation')}>
          <FeatherIcon name="trash-2" size={14} />
        </button>
      </div>
    </div>
  );
};

/**
 * Past conversations: pinned first, then by day. Search filters what is
 * loaded (the API has no text search); "Load more" pages through the rest.
 */
export default function HistoryPane({
  open, items, total, loading, error, onLoadMore, onRetry,
  activeId, onOpen, onNew, onDelete, onTogglePin,
  query, onQuery, searchRef, onClose,
}) {
  const { t } = useTranslation('aiExplorer');
  const q = query.trim().toLowerCase();
  const visible = q ? items.filter((i) => String(i.title || '').toLowerCase().includes(q)) : items;
  const groups = groupHistory(visible);
  const sections = [
    ['pinned', t('agent.pinned')], ['today', t('agent.today')], ['yesterday', t('agent.yesterday')],
    ['week', t('agent.previous7Days')], ['older', t('agent.older')],
  ];
  const hasMore = items.length < total;

  return (
    <aside className={`sa3-hist${open ? '' : ' hidden'}`} aria-label={t('agent.conversations')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="sa3-newbtn" style={{ flex: 1 }} onClick={onNew}>
          <FeatherIcon name="plus" size={16} />{t('agent.newChat')}
        </button>
        {onClose && (
          <button type="button" className="sa3-ibtn" onClick={onClose} aria-label={t('agent.hideHistory')}>
            <FeatherIcon name="x" size={16} />
          </button>
        )}
      </div>
      <label className="sa3-search">
        <FeatherIcon name="search" size={15} />
        <input ref={searchRef} value={query} onChange={(e) => onQuery(e.target.value)} placeholder={t('agent.searchConversations')} aria-label={t('agent.searchConversations')} />
        <kbd>⌘K</kbd>
      </label>
      <div className="sa3-hist-scroll">
        {sections.map(([key, label]) => groups[key].length > 0 && (
          <React.Fragment key={key}>
            <div className="sa3-grp">{label}</div>
            {groups[key].map((item) => (
              <Item key={item.id} item={item} active={item.id === activeId} onOpen={onOpen} onDelete={onDelete} onTogglePin={onTogglePin} t={t} />
            ))}
          </React.Fragment>
        ))}
        {error && (
          <div className="sa3-hist-note error">
            {t('agent.historyUnavailable')}
            <button type="button" onClick={onRetry}>{t('agent.retry')}</button>
          </div>
        )}
        {!error && !loading && items.length === 0 && <div className="sa3-hist-note">{t('agent.noConversations')}</div>}
        {!error && !loading && items.length > 0 && visible.length === 0 && <div className="sa3-hist-note">{t('agent.noMatches')}</div>}
        {loading && <div className="sa3-hist-note">{t('agent.loading')}</div>}
        {!loading && !error && hasMore && !q && (
          <div className="sa3-hist-note"><button type="button" onClick={onLoadMore}>{t('agent.loadMore')}</button></div>
        )}
      </div>
    </aside>
  );
}
